/**
 * Enhanced Error Boundary with detailed AppDynamics logging
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { View, Text, StyleSheet } from "react-native";
import LhButton from "./LhButton";
import { logComponentError } from "../utils/EnhancedErrorLogging";
import { logger } from "../utils/logger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

export class EnhancedErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Create safe props object for logging (exclude React components and functions)
    const safeProps = Object.entries(this.props).reduce((acc, [key, value]) => {
      // Skip functions and React elements
      if (typeof value === 'function' || React.isValidElement(value)) {
        acc[key] = '[React Component/Function]';
      } else if (typeof value === 'object' && value !== null) {
        // Check for DOM elements and React fiber properties
        const valueAsAny = value as any;
        if (valueAsAny.nodeType || 
            valueAsAny._reactInternalFiber || 
            valueAsAny.__reactFiber$qnubpgbrgei ||
            valueAsAny.$$typeof) {
          acc[key] = '[DOM Element or React Component]';
        } else {
          acc[key] = '[Complex Object]';
        }
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        acc[key] = value;
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    // Log detailed error to AppDynamics
    const detailedError = logComponentError(
      error,
      { componentStack: errorInfo?.componentStack ?? "" },
      this.props.componentName || "UnknownComponent",
      safeProps
    );

    this.setState({
      errorInfo,
      errorId: detailedError.errorId,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    logger.group("🚨 React Error Boundary");
    logger.error("Error:", error);
    logger.error("Component Stack:", errorInfo.componentStack);
    logger.error("Error ID:", detailedError.errorId);
    logger.groupEnd();
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <View style={styles.container}>
          <View style={styles.card}>
            <Text style={styles.title}>Something went wrong</Text>
            <Text style={styles.message}>
              An unexpected error occurred. The error has been reported and will
              be investigated.
            </Text>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <View style={styles.debugInfo}>
                <Text style={styles.debugTitle}>Debug Info:</Text>
                <Text style={styles.debugText}>
                  Error: {this.state.error.message}
                </Text>
                {this.state.errorId && (
                  <Text style={styles.debugText}>
                    Error ID: {this.state.errorId}
                  </Text>
                )}
              </View>
            )}

            <View style={styles.buttonContainer}>
              <LhButton
                text="Try Again"
                onPress={this.handleRetry}
                style={styles.retryButton}
              />
            </View>
          </View>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f8f8f8",
    padding: 20,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 24,
    width: "100%",
    maxWidth: 500,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#d62b1f",
    textAlign: "center",
  },
  message: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 24,
    color: "#333",
    lineHeight: 24,
  },
  debugInfo: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    borderRadius: 4,
    marginBottom: 20,
    width: "100%",
  },
  debugTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#666",
  },
  debugText: {
    fontSize: 12,
    color: "#666",
    fontFamily: "monospace",
    marginBottom: 4,
  },
  buttonContainer: {
    width: "100%",
    alignItems: "center",
  },
  retryButton: {
    backgroundColor: "#d62b1f",
    paddingHorizontal: 32,
    paddingVertical: 12,
  },
});

/**
 * Higher-order component to wrap components with error boundary
 */
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string,
  fallback?: ReactNode
) => {
  const WrappedWithErrorBoundary = (props: P) => (
    <EnhancedErrorBoundary
      componentName={componentName || WrappedComponent.name}
      fallback={fallback}
    >
      <WrappedComponent {...props} />
    </EnhancedErrorBoundary>
  );

  WrappedWithErrorBoundary.displayName = `withErrorBoundary(${
    componentName || WrappedComponent.displayName || WrappedComponent.name
  })`;

  return WrappedWithErrorBoundary;
};

/**
 * Hook to provide error reporting functionality to functional components
 */
export const useErrorReporting = (componentName: string) => {
  const reportError = React.useCallback(
    (error: Error, context?: any) => {
      logComponentError(error, { componentStack: "" }, componentName, context);
    },
    [componentName]
  );

  return { reportError };
};
